import { createFileRoute, Link } from '@tanstack/react-router';
import { CodeBlock, InfoCallout } from '../../components/documentation';
import styles from '../../components/documentation/DocContent.module.css';

export const Route = createFileRoute('/documentation/sdk-reference')({
  component: SdkReferencePage,
});

function SdkReferencePage() {
  return (
    <>
      <div className={styles.breadcrumb}>SDK</div>
      <h1 className={styles.pageTitle}>SDK Reference</h1>
      <p className={styles.pageSubtitle}>
        Create admission tokens with the Interlucent SDK or any language that
        supports Ed25519 signing.
      </p>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="installation">
          Installation
        </h2>
        <CodeBlock language="bash">
          {`npm install @interlucent/admission-sdk`}
        </CodeBlock>
        <p className={styles.prose}>
          Initialize the client with your project's secret key. The client
          handles signing and expiration.
        </p>
        <CodeBlock language="TypeScript">
{`import { AdmissionClient } from '@interlucent/admission-sdk';

const client = await AdmissionClient.create(
  process.env.INTERLUCENT_SECRET_KEY!
);`}
        </CodeBlock>

        <InfoCallout variant="warning">
          The secret key must only be used server-side. Never expose it in
          client-side code or commit it to version control.
        </InfoCallout>

        <InfoCallout variant="info">
          Find your project secret key in{' '}
          <strong>Console &gt; Project Settings &gt; API Keys</strong>.
        </InfoCallout>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="creating-tokens">
          Creating Tokens
        </h2>

        <h3 className={styles.subsectionTitle} id="minimal">
          Minimal
        </h3>
        <p className={styles.prose}>
          The simplest token: specifies the application and a 5-minute
          expiration. All tolerances use platform defaults.
        </p>
        <CodeBlock language="TypeScript">
{`const token = await client
  .createToken()
  .asBrowserAgent()
  .withApplication('PFfFuw')  // your app ID from the Console
  .expiresIn(300)
  .sign();`}
        </CodeBlock>
        <p className={styles.prose}>
          Pass this token to your frontend and set it on the{' '}
          <code className={styles.mono}>&lt;pixel-stream&gt;</code>{' '}
          component's <code className={styles.mono}>admission-token</code>{' '}
          attribute.
        </p>

        <h3 className={styles.subsectionTitle} id="production">
          Production
        </h3>
        <p className={styles.prose}>
          A fully configured token: specific version, 45-second GPU wait,
          2-minute reconnect grace, tagged for billing reconciliation.
        </p>
        <CodeBlock language="TypeScript">
{`const production = await client
  .createToken()
  .asBrowserAgent()
  .withApplication('PFfFuw')
  .withVersion('MrM')
  .withCustomArgs({ user_id: 'usr_abc', theme: 'dark' })
  .withQueueWaitTolerance(45)
  .withFlexiblePresenceAllowance(120)
  .withRendezvousTolerance(30)
  .withReference('order-789')
  .withSwiftJobRequest()
  .expiresIn(300)
  .sign();`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="low-latency">
          Low-latency
        </h3>
        <p className={styles.prose}>
          Fail fast on any delay. Tight tolerance windows ensure the user gets
          feedback quickly rather than waiting in an uncertain state.
        </p>
        <CodeBlock language="TypeScript">
{`const lowLatency = await client
  .createToken()
  .asBrowserAgent()
  .withApplication('PFfFuw')
  .withRendezvousTolerance(10)
  .withWebRtcNegotiationTolerance(5)
  .withSwiftJobRequest()
  .expiresIn(120)
  .sign();`}
        </CodeBlock>

        <h3 className={styles.subsectionTitle} id="resilient">
          Resilient
        </h3>
        <p className={styles.prose}>
          Survive network hiccups and GPU churn. Long tolerance windows keep the
          session alive through transient disruptions.
        </p>
        <CodeBlock language="TypeScript">
{`const resilient = await client
  .createToken()
  .asBrowserAgent()
  .withApplication('PFfFuw')
  .withTolerances({
    queue_wait_tolerance_seconds: 120,
    rendezvous_tolerance_seconds: 60,
    flexible_presence_allowance_seconds: 300,
    linger_tolerance_seconds: 30,
    webrtc_negotiation_tolerance_seconds: 15,
  })
  .withSwiftJobRequest()
  .withReference('session-resilient-test')
  .expiresIn(600)
  .sign();`}
        </CodeBlock>
      </section>

      {/* HIDDEN: Worker Agent Tokens and Other Languages sections
      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="worker-agent-tokens">
          Advanced: Worker Agent Tokens
        </h2>
        ...
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="other-languages">
          Other Languages
        </h2>
        ...
      </section>
      */}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle} id="next-steps">
          Next Steps
        </h2>
        <p className={styles.prose}>
          Once you have an admission token, pass it to your frontend to start
          streaming. See the{' '}
          <Link to="/documentation/streaming-guide">
            Streaming Guide
          </Link>{' '}
          to connect it to the{' '}
          <code className={styles.mono}>&lt;pixel-stream&gt;</code> component.
        </p>
      </section>
    </>
  );
}
